import { z } from 'zod';
import { docCategorySchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { isoDateSchema } from '../../common/schemas/money.schemas.js';
/**
 * Vehicle documents — photos, licence scans, external reports and invoices (plan §8; PP §8).
 *
 * Three product rules are visible in these shapes rather than only in the service:
 *
 *  - **There is no URL.** A document is fetched through `GET /api/documents/{id}/content` with the
 *    caller's bearer token and rendered from an object URL, so a file is never reachable by a link that
 *    leaks out of the session (plan §2.5). `storageKey` is the *server's* path inside the configured
 *    root; it is published so an export can be reconciled with the disk, and it is useless to a browser.
 *  - **A replacement never destroys the file it replaced.** `replacedById` points forwards at the row
 *    that superseded this one; `documents.list` returns only the current rows, and the superseded ones
 *    stay readable through their own id (PP §8: "preserve replacement history").
 *  - **Attaching a report changes nothing.** There is no field here that any equation reads. The
 *    inspection and the valuation move only when a person records a finding (PP AC12).
 */
export const documentSchema = z.object({
    id: uuidSchema,
    vehicleId: uuidSchema,
    category: docCategorySchema,
    /** What a person calls it; defaults to the original file name on upload. */
    title: z.string(),
    originalName: z.string(),
    /** Sniffed from the file's own magic bytes, never from what the browser declared. */
    mime: z.enum(['image/jpeg', 'image/png', 'application/pdf']),
    sizeBytes: z.number().int().min(0),
    sha256: z.string(),
    storageKey: z.string(),
    /** The date printed on the document, which is rarely the day it was scanned. */
    documentDate: isoDateSchema.nullable(),
    sourceProvider: z.string().nullable(),
    externalRef: z.string().nullable(),
    /** A licence expiry stays legible even when a newer scan exists (PP §8). */
    expiryDate: isoDateSchema.nullable(),
    costItemId: uuidSchema.nullable(),
    serviceEventId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    uploadedBy: uuidSchema.nullable(),
    uploadedByName: z.string().nullable(),
    /** Set when a newer upload superseded this row; current rows carry `null`. */
    replacedById: uuidSchema.nullable(),
    /** The row this one replaced, when it replaced anything. */
    replacesId: uuidSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** The summary a vehicle detail carries, so the dossier header knows what is attached. */
export const documentsSummarySchema = z.object({
    count: z.number().int().min(0),
    photoCount: z.number().int().min(0),
    missingPhotoCount: z.number().int().min(0),
});
export const listDocumentsInputSchema = z.object({
    vehicleId: uuidSchema,
});
export const listDocumentsOutputSchema = z.object({
    vehicleId: uuidSchema,
    /** **Current rows only.** A superseded row is reachable through `replacedById` on its successor. */
    items: z.array(documentSchema),
});
/**
 * PATCH semantics over the metadata only. The bytes are immutable: correcting a file is a *replace*,
 * which keeps both rows, rather than an edit that quietly rewrites history.
 */
export const updateDocumentMetaInputSchema = z.object({
    id: uuidSchema,
    category: docCategorySchema.optional(),
    title: z.string().trim().min(1, 'Name the document').max(200).optional(),
    documentDate: isoDateSchema.nullish(),
    sourceProvider: z.string().trim().max(200).nullish(),
    expiryDate: isoDateSchema.nullish(),
    notes: z.string().trim().max(2_000).nullish(),
    /** Links to the work this document evidences; both must belong to the same vehicle. */
    costItemId: uuidSchema.nullish(),
    serviceEventId: uuidSchema.nullish(),
});
export const photoSetInputSchema = z.object({
    vehicleId: uuidSchema,
});
/**
 * The prototype's photo-set indicator (plan §3.1): the five availability photos plus the licence,
 * **informational only**. Nothing in this platform is gated on it — the old photo gate went with the
 * old lifecycle — so it reports a state and never a refusal.
 */
export const photoSetOutputSchema = z.object({
    vehicleId: uuidSchema,
    present: z.array(docCategorySchema),
    missing: z.array(docCategorySchema),
});
//# sourceMappingURL=documents.schemas.js.map